// src/components/PatientForm.tsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Error403Page from "./Error_backend";
import { Patient } from "../interfaces/types";
import api from "../api.js";
import DeleteModal from "./DeleteModal";
import "../style/css/AppointmentForm.css";
import AlertModal from "./AlertModal";

interface PatientFormData {
  name: string;
  cellphone: string; // This will store the combined value
  phoneCountryCode: string; // New field for country code
  phoneNumber: string; // New field for local number
  email: string;
  birth_date: string;
  sex: string;
  weight: string;
  height: string;
  diseases: string;
  notes: string;
  reminder_hours: string;
}

const PatientForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<PatientFormData>({
    name: "",
    cellphone: "",
    phoneCountryCode: "+52", // Fixed country code
    phoneNumber: "", // User will enter 10 digits here
    email: "",
    birth_date: "",
    sex: "",
    weight: "",
    height: "",
    diseases: "",
    notes: "",
    reminder_hours: "24",
  });

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [reminderDays, setReminderDays] = useState<string>("1");
  const [reminderHoursInput, setReminderHoursInput] = useState<string>("0");
  const [noReminder, setNoReminder] = useState<boolean>(false);

  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: "success" | "error";
    title?: string;
    message: string;
  }>({ isOpen: false, type: "success", message: "" });
  // API base URL
  const API_BASE = "/patients";

  const parsePhoneNumber = (fullNumber: string) => {
    // If number starts with +52, extract the remaining part
    if (fullNumber.startsWith("+52")) {
      return {
        countryCode: "+52",
        localNumber: fullNumber.substring(3), // Remove +52
      };
    }
    // If no country code, assume it's a local number
    return {
      countryCode: "+52",
      localNumber: fullNumber,
    };
  };

  // Helper function to combine phone number
  const combinePhoneNumber = (countryCode: string, localNumber: string) => {
    return `${countryCode}${localNumber}`;
  };

  // Validate form data
  // Validate form data
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!noReminder) {
      if (!formData.reminder_hours || formData.reminder_hours === "0") {
        errors.reminder_hours = "El recordatorio debe ser al menos 1 hora";
      } else {
        const hours = parseInt(formData.reminder_hours, 10);
        if (isNaN(hours) || !Number.isInteger(hours)) {
          errors.reminder_hours = "Ingresa un número entero";
        } else if (hours < 1) {
          errors.reminder_hours = "Mínimo 1 hora";
        } else if (hours > 240) {
          errors.reminder_hours = "Máximo 240 horas (10 días)";
        }
      }
    }

    // REQUIRED FIELDS
    if (!formData.name.trim()) {
      errors.name = "El nombre es requerido";
    }

    if (!formData.phoneNumber.trim()) {
      errors.phoneNumber = "El número de teléfono es requerido";
    } else {
      const cleanNumber = formData.phoneNumber.replace(/\D/g, "");
      if (cleanNumber.length !== 10) {
        errors.phoneNumber = "El número debe tener 10 dígitos";
      } else if (!/^[0-9]{10}$/.test(cleanNumber)) {
        errors.phoneNumber = "Ingresa un número válido de 10 dígitos";
      }
    }

    // OPTIONAL FIELDS (Only validate format if they entered something)

    if (formData.birth_date) {
      const birthDate = new Date(formData.birth_date);
      const today = new Date();
      if (birthDate > today) {
        errors.birth_date = "La fecha de nacimiento no puede ser futura";
      }
    }

    if (formData.weight.trim()) {
      const weight = parseFloat(formData.weight);
      if (isNaN(weight) || weight <= 0 || weight > 300) {
        errors.weight = "El peso debe ser un número válido entre 0.1 y 300 kg";
      }
    }

    if (formData.height.trim()) {
      const height = parseFloat(formData.height);
      if (isNaN(height) || height <= 0 || height > 300) {
        errors.height = "La altura debe ser un número válido entre 1 y 300 cm";
      }
    }

    if (
      formData.email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    ) {
      errors.email = "El formato del email no es válido";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    setError(null);

    try {
      const response = await api.delete(`${API_BASE}/${id}/`);

      if (!response) {
        throw new Error("Error al eliminar paciente");
      }

      navigate("/patients");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ocurrió un error");
    } finally {
      setDeleting(false);
    }
  };

  // Fetch patient data if in edit mode
  useEffect(() => {
    const fetchPatient = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`${API_BASE}/${id}/`);
        if (!response) {
          throw new Error("Failed to fetch patient");
        }

        const patientData = await response.data;

        const { countryCode, localNumber } = parsePhoneNumber(
          patientData.cellphone || "",
        );
        const totalHours = patientData.reminder_setting?.reminder_hours ?? 24;

        if (totalHours === 0) {
          setNoReminder(true);
          setReminderDays("0");
          setReminderHoursInput("0");
        } else {
          setNoReminder(false);
          setReminderDays(String(Math.floor(totalHours / 24)));
          setReminderHoursInput(String(totalHours % 24));
        }

        // Format the data for the form
        setFormData({
          name: patientData.name || "",
          cellphone: patientData.cellphone || "",
          phoneCountryCode: countryCode,
          phoneNumber: localNumber,
          email: patientData.email || "",
          birth_date: patientData.birth_date || "",
          sex: patientData.sex || "",
          weight: patientData.weight?.toString() || "",
          height: patientData.height?.toString() || "",
          diseases: patientData.diseases || "",
          notes: patientData.notes || "",
          reminder_hours: String(totalHours),
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchPatient();
  }, [id]);

  const handleNoReminderToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setNoReminder(isChecked);

    if (isChecked) {
      // Set to 0 to send to backend
      setFormData((prev) => ({ ...prev, reminder_hours: "0" }));
      // Clear any existing errors
      if (formErrors.reminder_hours) {
        setFormErrors((prev) => ({ ...prev, reminder_hours: "" }));
      }
    } else {
      // If turned back on, recalculate from inputs, default to 24h if both are 0
      let d = parseInt(reminderDays || "0", 10);
      let h = parseInt(reminderHoursInput || "0", 10);
      let total = d * 24 + h;

      if (total === 0) {
        total = 24;
        setReminderDays("1");
        setReminderHoursInput("0");
      }
      setFormData((prev) => ({ ...prev, reminder_hours: String(total) }));
    }
  };

  const handleReminderChange = (field: "days" | "hours", value: string) => {
    // Only allow non-negative integers
    const clean = value.replace(/\D/g, "");

    const days = field === "days" ? clean : reminderDays;
    const hrs = field === "hours" ? clean : reminderHoursInput;

    if (field === "days") setReminderDays(clean);
    if (field === "hours") setReminderHoursInput(clean);

    const total = parseInt(days || "0", 10) * 24 + parseInt(hrs || "0", 10);

    setFormData((prev) => ({ ...prev, reminder_hours: String(total) }));

    // Clear error live
    if (formErrors.reminder_hours) {
      setFormErrors((prev) => ({ ...prev, reminder_hours: "" }));
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;

    // If phone number is being changed, clean it (remove non-digits)
    if (name === "phoneNumber") {
      const cleanValue = value.replace(/\D/g, ""); // Remove non-digits
      setFormData((prev) => ({
        ...prev,
        [name]: cleanValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const formatReminderText = (value: string): string => {
    const h = parseInt(value, 10);
    if (isNaN(h) || h <= 0) return "";

    const days = Math.floor(h / 24);
    const hours = h % 24;

    const daysText = days > 0 ? `${days} ${days === 1 ? "día" : "días"}` : "";
    const hoursText =
      hours > 0 ? `${hours} ${hours === 1 ? "hora" : "horas"}` : "";

    if (daysText && hoursText) {
      return `El recordatorio se enviará ${daysText} y ${hoursText} antes de la cita`;
    } else if (daysText) {
      return `El recordatorio se enviará ${daysText} antes de la cita`;
    } else {
      return `El recordatorio se enviará ${hoursText} antes de la cita`;
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      setError("Por favor, corrija los errores en el formulario");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Combine phone number before sending
      const combinedPhone = combinePhoneNumber(
        formData.phoneCountryCode,
        formData.phoneNumber,
      );

      const dataToSave = {
        ...formData,
        cellphone: combinedPhone,
        phoneCountryCode: undefined,
        phoneNumber: undefined,
        reminder_hours: undefined,
        weight: formData.weight.trim() ? parseFloat(formData.weight) : null,
        height: formData.height.trim() ? parseFloat(formData.height) : null,
        birth_date: formData.birth_date ? formData.birth_date : null,
        sex: formData.sex ? formData.sex : null,
        email: formData.email.trim() ? formData.email.trim() : null,
      };

      const cleanedData = Object.fromEntries(
        Object.entries(dataToSave).filter(([_, v]) => v !== undefined),
      );

      let response;
      if (isEditMode) {
        response = await api.put(`${API_BASE}/${id}/`, cleanedData);
      } else {
        response = await api.post(`${API_BASE}/`, cleanedData);
      }

      if (!response) {
        throw new Error(
          `Failed to ${isEditMode ? "update" : "create"} patient`,
        );
      }
      const patientId = isEditMode ? id : response.data.id;

      // Uses PUT (upsert-style) — adjust endpoint to match your router
      await api.put(`/patients/${patientId}/reminder-setting/`, {
        reminder_hours: parseInt(formData.reminder_hours, 10),
      });

      navigate("/patients");
    } catch (err: any) {
      if (err.response?.status === 400) {
        const errorData = err.response.data;
        if (errorData.details) {
          const fieldErrors = errorData.details;

          // Check specifically if the backend returned a cellphone (duplicate) error
          if (fieldErrors.cellphone) {
            setAlertModal({
              isOpen: true,
              type: "error",
              title: "Teléfono no disponible",
              message: fieldErrors.cellphone[0],
            });
            // Also map the backend "cellphone" error to the frontend "phoneNumber" input so it turns red
            setFormErrors((prev) => ({
              ...prev,
              phoneNumber: fieldErrors.cellphone[0],
            }));
          } else {
            let errorMessage = "Por favor corrige los siguientes campos:\n";
            Object.keys(fieldErrors).forEach((field) => {
              const fieldName =
                field === "weight"
                  ? "peso"
                  : field === "height"
                    ? "altura"
                    : field;
              errorMessage += `• ${fieldName}: ${fieldErrors[field].join(", ")}\n`;
            });
            setAlertModal({
              isOpen: true,
              type: "error",
              title: "Error de validación",
              message: errorMessage,
            });
          }
        } else if (errorData.message) {
          setAlertModal({
            isOpen: true,
            type: "error",
            title: "Error",
            message: errorData.message,
          });
        } else {
          setAlertModal({
            isOpen: true,
            type: "error",
            title: "Error",
            message: "Error de validación en el servidor",
          });
        }
      } else if (err.response?.status === 401) {
        setAlertModal({
          isOpen: true,
          type: "error",
          title: "No autorizado",
          message: "Por favor, inicie sesión.",
        });
      } else if (err.response?.status === 403) {
        setAlertModal({
          isOpen: true,
          type: "error",
          title: "Permiso denegado",
          message: "No tiene permiso para realizar esta acción.",
        });
      } else if (err.response?.status >= 500) {
        setAlertModal({
          isOpen: true,
          type: "error",
          title: "Error interno",
          message: "Error del servidor. Por favor, intente más tarde.",
        });
      } else {
        setAlertModal({
          isOpen: true,
          type: "error",
          title: "Error",
          message: err.message || "An error occurred",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleBackClick = () => {
    navigate("/patients"); // Adjust to your patients list route
  };

  // Calculate age from date of birth
  const calculateAge = (dateString: string): string => {
    if (!dateString) return "";

    const birthDate = new Date(dateString);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return `${age} años`;
  };

  // Show loading state
  if (loading) {
    return (
      <div className="form-entire-container-form-appointment">
        <div className="form-container-form-appointment">
          <div className="loading-message-form-appointment">
            <i className="fas fa-spinner fa-spin"></i> Cargando paciente...
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && isEditMode) {
    return (
      <Error403Page
        code={500}
        title="Recurso no disponible."
        message="Hemos tenido problemas para acceder al recurso."
        homeHref="/"
        loginHref="/login"
        supportHref="/support"
        onHomeClick={(e) => {
          e.preventDefault();
          navigate("/");
        }}
        onLoginClick={(e) => {
          e.preventDefault();
          navigate("/login");
        }}
      />
    );
  }

  const titleText = isEditMode ? "Editar Paciente" : "Crear Nuevo Paciente";

  return (
    <div className="form-entire-container-form-appointment">
      <div className="form-container-form-appointment">
        <div className="header-actions-form-appointment">
          <button
            className="back-link-form-appointment"
            onClick={handleBackClick}
            style={{ background: "none", border: "none", cursor: "pointer" }}
          >
            <i className="fas fa-arrow-left"></i> Volver a Pacientes
          </button>
          <h2 style={{ margin: 0, fontSize: "1.5rem" }}>
            <i
              className="fas fa-user"
              style={{ color: "var(--color-accent)" }}
            ></i>{" "}
            {titleText}
          </h2>
          <span></span>
        </div>

        <div className="form-card-form-appointment">
          <h3 style={{ marginBottom: "1.5rem", textAlign: "center" }}>
            {isEditMode ? `Editando: ${formData.name}` : "Nuevo paciente"}
          </h3>

          {error && (
            <div className="message-box-form-appointment message-error-form-appointment">
              <i className="fas fa-exclamation-triangle"></i>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Personal Information */}
            <div className="form-group-form-appointment">
              <label htmlFor="name">Nombre Completo *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                disabled={saving || deleting}
                placeholder="Ej: Juan Pérez García"
                className={formErrors.name ? "input-error" : ""}
              />
              {formErrors.name && (
                <span className="field-error">{formErrors.name}</span>
              )}
            </div>

            <div className="grid-half-form-appointment">
              <div className="form-group-form-appointment">
                <label htmlFor="phoneNumber">Teléfono *</label>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div
                    style={{
                      padding: "0.70rem",
                      border: "1px solid var(--neutral-300)",
                      borderRight: "none",
                      borderTopLeftRadius: "10px",
                      borderBottomLeftRadius: "10px",
                      fontSize: "0.9rem",
                      color: "var(--neutral-700)",
                    }}
                  >
                    +52
                  </div>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    required
                    disabled={saving || deleting}
                    placeholder="7714512345"
                    className={formErrors.phoneNumber ? "input-error" : ""}
                    style={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                      flex: 1,
                    }}
                    maxLength={10}
                  />
                </div>
                {formErrors.phoneNumber && (
                  <span className="field-error">{formErrors.phoneNumber}</span>
                )}
                <div
                  style={{
                    marginTop: "0.25rem",
                    fontSize: "0.875rem",
                    color: "var(--color-secondary)",
                  }}
                >
                  Ej: 7714512345 (10 dígitos sin espacios)
                </div>
              </div>

              <div className="form-group-form-appointment">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={saving || deleting}
                  placeholder="Ej: juan.perez@email.com"
                  className={formErrors.email ? "input-error" : ""}
                />
                {formErrors.email && (
                  <span className="field-error">{formErrors.email}</span>
                )}
              </div>
            </div>

            <div className="grid-half-form-appointment">
              <div className="form-group-form-appointment">
                <label htmlFor="birth_date">Fecha de Nacimiento</label>
                <input
                  type="date"
                  id="birth_date"
                  name="birth_date"
                  value={formData.birth_date}
                  onChange={handleChange}
                  disabled={saving || deleting}
                  max={new Date().toISOString().split("T")[0]} // Prevent future dates
                  className={formErrors.birth_date ? "input-error" : ""}
                />
                {formErrors.birth_date && (
                  <span className="field-error">{formErrors.birth_date}</span>
                )}
                {formData.birth_date && (
                  <div
                    style={{
                      marginTop: "var(--space-xs)",
                      fontSize: "0.875rem",
                      color: "var(--color-secondary)",
                    }}
                  >
                    Edad: {calculateAge(formData.birth_date)}
                  </div>
                )}
              </div>

              <div className="form-group-form-appointment">
                <label htmlFor="sex">Sexo</label>
                <select
                  id="sex"
                  name="sex"
                  value={formData.sex}
                  onChange={handleChange}
                  disabled={saving || deleting}
                  className={formErrors.sex ? "input-error" : ""}
                >
                  <option value="">Selecciona una opción</option>
                  <option value="M">Masculino</option>
                  <option value="F">Femenino</option>
                  <option value="O">Otro</option>
                </select>
                {formErrors.sex && (
                  <span className="field-error">{formErrors.sex}</span>
                )}
              </div>
            </div>

            {/* Medical Information */}
            <div className="grid-half-form-appointment">
              <div className="form-group-form-appointment">
                <label htmlFor="weight">Peso (kg)</label>
                <input
                  type="number"
                  id="weight"
                  name="weight"
                  value={formData.weight}
                  onChange={handleChange}
                  disabled={saving || deleting}
                  placeholder="Ej: 70.5"
                  step="0.1"
                  min="0.1"
                  max="300"
                  className={formErrors.weight ? "input-error" : ""}
                />
                {formErrors.weight && (
                  <span className="field-error">{formErrors.weight}</span>
                )}
              </div>

              <div className="form-group-form-appointment">
                <label htmlFor="height">Altura (cm)</label>
                <input
                  type="number"
                  id="height"
                  name="height"
                  value={formData.height}
                  onChange={handleChange}
                  disabled={saving || deleting}
                  placeholder="Ej: 175"
                  step="0.1"
                  min="1"
                  max="300"
                  className={formErrors.height ? "input-error" : ""}
                />
                {formErrors.height && (
                  <span className="field-error">{formErrors.height}</span>
                )}
              </div>
            </div>

            <div className="form-group-form-appointment">
              <label htmlFor="diseases">
                Diagnóstico Principal / Padecimientos
              </label>
              <textarea
                id="diseases"
                name="diseases"
                rows={3}
                placeholder="Ej: Dolor lumbar crónico, hipertensión arterial..."
                value={formData.diseases}
                onChange={handleChange}
                disabled={saving || deleting}
              ></textarea>
            </div>
            <div className="form-group-form-appointment">
              <label>
                <i
                  className="fas fa-bell"
                  style={{ color: "var(--color-accent)" }}
                ></i>{" "}
                Recordatorio de cita
              </label>

              {/* Checkbox to disable reminder */}
              <div
                style={{
                  marginBottom: "1rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  backgroundColor: noReminder
                    ? "var(--neutral-100)"
                    : "transparent",
                  padding: noReminder ? "0.5rem" : "0",
                  borderRadius: "8px",
                  transition: "all 0.2s ease",
                }}
              >
                <input
                  type="checkbox"
                  id="no_reminder"
                  checked={noReminder}
                  onChange={handleNoReminderToggle}
                  disabled={saving || deleting}
                  style={{ width: "auto", margin: 0, cursor: "pointer" }}
                />
                <label
                  htmlFor="no_reminder"
                  style={{
                    margin: 0,
                    fontWeight: "normal",
                    cursor: "pointer",
                    color: noReminder
                      ? "var(--color-error)"
                      : "var(--neutral-700)",
                  }}
                >
                  No enviar recordatorio a este paciente
                </label>
              </div>

              {/* Only show inputs if we ARE sending a reminder */}
              {!noReminder && (
                <>
                  <div
                    style={{
                      display: "flex",
                      gap: "0.75rem",
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <label
                        htmlFor="reminder_days"
                        style={{
                          fontSize: "0.8rem",
                          color: "var(--color-secondary)",
                          marginBottom: "0.25rem",
                          display: "block",
                        }}
                      >
                        Días
                      </label>
                      <input
                        type="number"
                        id="reminder_days"
                        value={reminderDays}
                        onChange={(e) =>
                          handleReminderChange("days", e.target.value)
                        }
                        disabled={saving || deleting}
                        placeholder="0"
                        min={0}
                        max={10}
                        step={1}
                        onKeyDown={(e) =>
                          [".", ",", "-"].includes(e.key) && e.preventDefault()
                        }
                        className={
                          formErrors.reminder_hours ? "input-error" : ""
                        }
                      />
                    </div>

                    <div style={{ flex: 1 }}>
                      <label
                        htmlFor="reminder_hours_input"
                        style={{
                          fontSize: "0.8rem",
                          color: "var(--color-secondary)",
                          marginBottom: "0.25rem",
                          display: "block",
                        }}
                      >
                        Horas
                      </label>
                      <input
                        type="number"
                        id="reminder_hours_input"
                        value={reminderHoursInput}
                        onChange={(e) =>
                          handleReminderChange("hours", e.target.value)
                        }
                        disabled={saving || deleting}
                        placeholder="0"
                        min={0}
                        max={23}
                        step={1}
                        onKeyDown={(e) =>
                          [".", ",", "-"].includes(e.key) && e.preventDefault()
                        }
                        className={
                          formErrors.reminder_hours ? "input-error" : ""
                        }
                      />
                    </div>
                  </div>

                  {formErrors.reminder_hours && (
                    <span className="field-error">
                      {formErrors.reminder_hours}
                    </span>
                  )}

                  {formData.reminder_hours && !formErrors.reminder_hours && (
                    <div
                      style={{
                        marginTop: "0.35rem",
                        fontSize: "0.875rem",
                        color: "var(--color-accent)",
                        fontWeight: 500,
                        display: "flex",
                        alignItems: "center",
                        gap: "0.35rem",
                      }}
                    >
                      <i className="fas fa-info-circle"></i>
                      {formatReminderText(formData.reminder_hours)}
                    </div>
                  )}

                  <div
                    style={{
                      marginTop: "0.25rem",
                      fontSize: "0.8rem",
                      color: "var(--color-secondary)",
                    }}
                  >
                    Máximo 10 días · Total:{" "}
                    <strong>{formData.reminder_hours || "0"} horas</strong>
                  </div>
                </>
              )}
            </div>

            <div className="form-group-form-appointment">
              <label
                htmlFor="notes"
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                <i
                  className="fas fa-sticky-note"
                  style={{ color: "var(--color-accent)" }}
                ></i>
                Notas Adicionales / Observaciones
              </label>
              <textarea
                id="notes"
                name="notes"
                rows={4}
                placeholder="Ej: 
• Alergias: Penicilina
• Medicamentos actuales: Metformina 500mg
• Historial familiar: Diabetes tipo 2
• Observaciones: Paciente responde mejor a terapia manual"
                value={formData.notes}
                onChange={handleChange}
                disabled={saving || deleting}
                style={{
                  fontFamily: "monospace",
                  fontSize: "0.9rem",
                }}
              ></textarea>
              <div
                style={{
                  marginTop: "var(--space-xs)",
                  fontSize: "0.875rem",
                  color: "var(--color-secondary)",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <span>Información adicional relevante</span>
                <span>{formData.notes.length}/2000 caracteres</span>
              </div>
            </div>

            <hr
              style={{
                borderColor: "var(--neutral-200)",
                margin: "var(--space-md) 0",
              }}
            />

            <div className="action-footer-form-appointment">
              {isEditMode && (
                <div>
                  <button
                    type="button"
                    className="btn--delete-form-appointment"
                    onClick={() => setIsDeleteModalOpen(true)}
                    disabled={saving || deleting}
                  >
                    <i className="fas fa-trash-alt"></i>
                    {deleting ? "Eliminando..." : "Eliminar Paciente"}
                  </button>
                  <DeleteModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    onConfirm={handleDeleteConfirm}
                    title="¿Eliminar paciente?"
                    description="Esto eliminará el paciente de forma permanente y sus citas."
                    confirmText="Eliminar paciente"
                    cancelText="Mantener paciente"
                  />
                </div>
              )}

              <button
                type="submit"
                className={`btn btn--primary btn--large btn--save-form-appointment ${
                  !isEditMode ? "btn--full-width-in-create" : ""
                }`}
                disabled={saving || deleting}
              >
                <i className="fas fa-save"></i>{" "}
                {saving
                  ? isEditMode
                    ? "Guardando..."
                    : "Creando..."
                  : isEditMode
                    ? "Guardar Cambios"
                    : "Crear Paciente"}
              </button>
            </div>
          </form>
        </div>
      </div>
      {/* ADD THE ALERT MODAL HERE */}
      <AlertModal
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default PatientForm;
